import CloseIcon from "@mui/icons-material/Close";
import MinimizeIcon from "@mui/icons-material/Minimize";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, IconButton, Tooltip } from "@mui/material";

import { getHeaderBackgroundColor, HEADER_HEIGHT } from "../constants";
import type { ImpersonationSession } from "../types";

interface FloatingWindowHeaderProps {
  session: ImpersonationSession;
  isClosing: boolean;
  isWarning: boolean;
  error: string | undefined;
  remainingMs: number;
  totalWidth: number;
  handleDragStart: (event: React.PointerEvent) => void;
  handleDragMove: (event: React.PointerEvent) => void;
  handleDragEnd: (event: React.PointerEvent) => void;
  handleRefresh: () => void;
  handleClose: () => void;
  minimizeSession: (id: string) => void;
}

export function FloatingWindowHeader(props: FloatingWindowHeaderProps) {
  const {
    session,
    isClosing,
    isWarning,
    error,
    remainingMs,
    totalWidth,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleRefresh,
    handleClose,
    minimizeSession,
  } = props;
  const remainingMinutes = Math.max(0, Math.ceil(remainingMs / 60_000));
  const headerTitle = isClosing
    ? "Closing..."
    : `${session.targetName} — ${session.targetId} (${remainingMinutes}m)`;

  return (
    <Tooltip title={isWarning ? "Session expires soon" : ""}>
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          height: HEADER_HEIGHT,
          paddingX: 1.5,
          backgroundColor: getHeaderBackgroundColor(isClosing, error, isWarning),
          color: theme.palette.common.white,
          borderRadius: "8px 8px 0 0",
          cursor: "grab",
          width: totalWidth,
          boxSizing: "border-box",
          transition: "background-color 0.3s",
        })}
        onPointerDown={handleDragStart}
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
      >
        <Box
          component="span"
          sx={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: 600,
            fontSize: "0.75rem",
            pointerEvents: "none",
          }}
        >
          {headerTitle}
        </Box>
        <IconButton
          size="small"
          aria-label={`Refresh impersonation session for ${session.targetName}`}
          disabled={isClosing}
          sx={(theme) => ({ color: theme.palette.common.white, padding: 0.25 })}
          onClick={handleRefresh}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label={`Minimize impersonation session for ${session.targetName}`}
          disabled={isClosing}
          sx={(theme) => ({ color: theme.palette.common.white, padding: 0.25 })}
          onClick={() => {
            minimizeSession(session.id);
          }}
        >
          <MinimizeIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label={`Close impersonation session for ${session.targetName}`}
          disabled={isClosing}
          sx={(theme) => ({ color: theme.palette.common.white, padding: 0.25 })}
          onClick={handleClose}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Tooltip>
  );
}
